import React, { useMemo } from "react";
import { Alert, Select, Table, Tag } from "antd";
import { type ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { toCheckoutCartId } from "@fitness-app/data-models/entities/CheckoutFunnelStats";

import { formatNumber, formatPercent, formatPln } from "../marketingFormat";
import {
  buildCheckoutFunnelDays,
  type CheckoutFunnelDay,
  type CheckoutFunnelFilters,
  type CheckoutFunnelIssueSummary,
  groupCheckoutFunnelCarts,
  listCheckoutFunnelVariants,
  NO_VARIANT,
  stepRate,
  sumCheckoutFunnelDays,
  summarizeCheckoutFunnelIssues,
} from "./checkoutFunnelMetrics";
import { isCheckoutFunnelUnavailable, useCheckoutFunnel } from "./useCheckoutFunnel";

/** Tyle dni analytics-service czyta z PostHoga w jednym zapytaniu. */
export const MAX_CHECKOUT_FUNNEL_RANGE_DAYS = 92;

const FILTER_KEYS = ["cart", "checkoutVariant", "lpVariant"] as const;
const NUMERIC_CELL_CLASS_NAME = "tabular-nums";

interface Props {
  from: string;
  to: string;
}

const rangeDays = (from: string, to: string) =>
  Math.floor((Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / 86_400_000) + 1;

const readFilters = (searchParams: URLSearchParams): CheckoutFunnelFilters => {
  const filters: CheckoutFunnelFilters = Object.fromEntries(
    FILTER_KEYS.flatMap((key) => (searchParams.get(key) ? [[key, searchParams.get(key)]] : [])),
  );
  // Linki sprzed grupowania niosą ścieżkę zamiast id koszyka.
  if (filters.cart?.startsWith("/")) filters.cart = toCheckoutCartId(filters.cart);
  return filters;
};

const withRate = (value: number, entries: number) => {
  const rate = stepRate(value, entries);
  return (
    <span className={NUMERIC_CELL_CLASS_NAME}>
      {formatNumber(value)}
      {rate !== null && value > 0 ? <span className="ml-1 text-xs text-gray-400">{formatPercent(rate)}</span> : null}
    </span>
  );
};

const problem = (value: number) =>
  value > 0 ? <span className="tabular-nums font-medium text-red-600">{formatNumber(value)}</span> : "0";

export const CheckoutFunnelTab: React.FC<Props> = ({ from, to }) => {
  const { t } = useTranslation("dashboard");
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const filters = useMemo(() => readFilters(new URLSearchParams(search)), [search]);
  const rangeAllowed = rangeDays(from, to) <= MAX_CHECKOUT_FUNNEL_RANGE_DAYS;
  const { data, error, isLoading, isFetching } = useCheckoutFunnel({ from, to }, rangeAllowed);

  const setFilter = (key: (typeof FILTER_KEYS)[number], value: string | undefined) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      // Wersje zależą od koszyka — po zmianie koszyka stary wybór mógłby nic nie pokazać.
      if (key === "cart") FILTER_KEYS.slice(1).forEach((variantKey) => next.delete(variantKey));
      return next;
    });

  const days = useMemo(
    () => (data ? buildCheckoutFunnelDays(data, filters, { from, to }) : []),
    [data, filters, from, to],
  );
  const total = useMemo(() => sumCheckoutFunnelDays(days), [days]);
  const issues = useMemo(() => (data ? summarizeCheckoutFunnelIssues(data.issues, filters) : []), [data, filters]);

  const cartOptions = useMemo(
    () =>
      (data ? groupCheckoutFunnelCarts(data) : []).map((group) => ({
        label: group.productName ?? t("marketingAnalytics.checkout.filters.otherProduct"),
        title: group.productName ?? undefined,
        options: group.carts.map((cart) => ({
          value: cart.id,
          label: `${t(`marketingAnalytics.checkout.cartKinds.${cart.kind}`)} ${cart.slug}${
            cart.status && cart.status !== "active" ? ` (${t("marketingAnalytics.checkout.filters.inactive")})` : ""
          }`,
          entries: cart.entries,
        })),
      })),
    [data, t],
  );

  const variantLabel = (key: "checkoutVariant" | "lpVariant", value: string | null) =>
    value === null
      ? t(`marketingAnalytics.checkout.variants.${key}.none`)
      : t(`marketingAnalytics.checkout.variants.${key}.${value}`, { defaultValue: value });

  const variantOptions = (key: "checkoutVariant" | "lpVariant") =>
    (data ? listCheckoutFunnelVariants(data, key, filters.cart) : []).map((value) => ({
      value: value ?? NO_VARIANT,
      label: variantLabel(key, value),
    }));

  const columns = useMemo<ColumnsType<CheckoutFunnelDay>>(() => {
    const step = (
      key: keyof CheckoutFunnelDay,
      render: (value: number, row: CheckoutFunnelDay) => React.ReactNode,
    ) => ({
      title: t(`marketingAnalytics.checkout.columns.${key}`),
      dataIndex: key,
      key,
      align: "right" as const,
      className: NUMERIC_CELL_CLASS_NAME,
      render,
    });
    return [
      {
        title: t("marketingAnalytics.checkout.columns.day"),
        dataIndex: "day",
        key: "day",
        fixed: "left",
        width: 110,
      },
      step("entries", (value) => formatNumber(value)),
      step("fieldCompleted", (value, row) => withRate(value, row.entries)),
      step("methodSelected", (value, row) => withRate(value, row.entries)),
      step("payClicked", (value, row) => withRate(value, row.entries)),
      step("payBlocked", problem),
      step("blikPending", (value) => formatNumber(value)),
      step("paid", (value, row) => withRate(value, row.entries)),
      step("failed", problem),
      step("canceled", (value) => formatNumber(value)),
      step("pending", (value) => formatNumber(value)),
      step("blikTimeout", problem),
      step("orderFailed", problem),
      step("revenue", (value) => formatPln(value)),
    ];
  }, [t]);

  const issueColumns = useMemo<ColumnsType<CheckoutFunnelIssueSummary>>(
    () => [
      {
        title: t("marketingAnalytics.checkout.issues.kind"),
        dataIndex: "kind",
        key: "kind",
        render: (kind: string) => <Tag color="red">{t(`marketingAnalytics.checkout.issues.kinds.${kind}`)}</Tag>,
      },
      {
        title: t("marketingAnalytics.checkout.issues.detail"),
        dataIndex: "detail",
        key: "detail",
        render: (detail: string | null) =>
          detail ? t(`marketingAnalytics.checkout.issues.details.${detail}`, { defaultValue: detail }) : "-",
      },
      {
        title: t("marketingAnalytics.checkout.issues.code"),
        dataIndex: "code",
        key: "code",
        render: (code: string | null) =>
          code ? t(`marketingAnalytics.checkout.issues.codes.${code}`, { defaultValue: code }) : "-",
      },
      {
        title: t("marketingAnalytics.checkout.issues.events"),
        dataIndex: "events",
        key: "events",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
      },
      {
        title: t("marketingAnalytics.checkout.issues.persons"),
        dataIndex: "persons",
        key: "persons",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
      },
    ],
    [t],
  );

  if (!rangeAllowed) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t("marketingAnalytics.checkout.rangeTooLong", { days: MAX_CHECKOUT_FUNNEL_RANGE_DAYS })}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <Select
          aria-label={t("marketingAnalytics.checkout.filters.cart")}
          allowClear
          showSearch
          className="w-full sm:w-96"
          placeholder={t("marketingAnalytics.checkout.filters.allCarts")}
          options={cartOptions}
          optionFilterProp="label"
          optionRender={(option) => (
            <div className="flex justify-between gap-3">
              <span className="truncate">{option.label}</span>
              <span className="tabular-nums text-gray-400">
                {formatNumber((option.data as { entries?: number }).entries)}
              </span>
            </div>
          )}
          value={filters.cart}
          onChange={(value?: string) => setFilter("cart", value)}
        />
        <Select
          aria-label={t("marketingAnalytics.checkout.filters.checkoutVariant")}
          allowClear
          className="w-full sm:w-52"
          placeholder={t("marketingAnalytics.checkout.filters.checkoutVariant")}
          options={variantOptions("checkoutVariant")}
          value={filters.checkoutVariant}
          onChange={(value?: string) => setFilter("checkoutVariant", value)}
        />
        <Select
          aria-label={t("marketingAnalytics.checkout.filters.lpVariant")}
          allowClear
          className="w-full sm:w-52"
          placeholder={t("marketingAnalytics.checkout.filters.lpVariant")}
          options={variantOptions("lpVariant")}
          value={filters.lpVariant}
          onChange={(value?: string) => setFilter("lpVariant", value)}
        />
      </div>
      {error ? (
        <Alert
          type={isCheckoutFunnelUnavailable(error) ? "info" : "error"}
          showIcon
          message={t(
            isCheckoutFunnelUnavailable(error)
              ? "marketingAnalytics.checkout.unavailable"
              : "marketingAnalytics.checkout.loadError",
          )}
        />
      ) : null}
      <Table<CheckoutFunnelDay>
        rowKey="day"
        size="small"
        bordered
        pagination={false}
        loading={isLoading || isFetching}
        columns={columns}
        dataSource={days}
        scroll={{ x: "max-content" }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="font-semibold">
              {columns.map((column, index) => {
                const key = column.key as keyof CheckoutFunnelDay;
                const render = (column as { render?: (value: number, row: CheckoutFunnelDay) => React.ReactNode })
                  .render;
                return (
                  <Table.Summary.Cell key={String(key)} index={index} align={column.align}>
                    {key === "day"
                      ? t("marketingAnalytics.checkout.total")
                      : (render?.(total[key] as number, total) ?? total[key])}
                  </Table.Summary.Cell>
                );
              })}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      <Table<CheckoutFunnelIssueSummary>
        title={() => t("marketingAnalytics.checkout.issues.title")}
        rowKey="key"
        size="small"
        pagination={false}
        loading={isLoading || isFetching}
        columns={issueColumns}
        dataSource={issues}
        locale={{ emptyText: t("marketingAnalytics.checkout.issues.empty") }}
      />
    </div>
  );
};
